/**
 * Usage dashboard — what this account and, for an admin, this organisation have
 * spent this cycle. A port of `bimgo-ai-web`'s `Dashboard` page.
 *
 * **Role access is the shape of this screen, not a detail.** Everyone sees their
 * own usage. The organisation half — every member's spend, the org's trend —
 * appears only for a caller holding `org:read`, and the tabs themselves only
 * appear when there is a second tab to show; a lone "Personal Usage" tab is
 * furniture. Frontend gating is a UX accelerator: api-v2's `require_action` is
 * what actually refuses, and the org calls answer **403** to anyone else. That
 * 403 is reported as a permission problem rather than a failure, because it is
 * one — the session is fine.
 *
 * The numbers are read strictly (`lib/usage`): a **null limit means unlimited**,
 * so those quotas render as a figure with no progress bar rather than a bar at
 * 0% ("nothing used") or 100% ("exhausted"), both of which would be lies.
 *
 * The organisation tab also carries the wallet's **credit spend per member**
 * (#64), the same card Settings ▸ Billing & Plan shows. Requests, tokens and
 * credits are three views of one cycle, and an admin asking "who is spending
 * this" should not have to work out which screen answers in which unit. The
 * credit figures are wallet (PAYG) draws — a seat member inside their prepaid
 * allowance reads 0 there and a real number in the tables above it.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { hasAction, type PermissionsState } from "../hooks/usePermissions";
import { API_BASE_URL, isConfigured } from "../lib/config";
import { statusOf } from "../lib/threads";
import {
  addOneDay,
  formatDateRange,
  getOrgUsage,
  getUsageStatus,
  getUsageTimeseries,
  remaining,
  usedPercent,
  type OrgUsageSummary,
  type Range,
  type UsageDateRange,
  type UsageStatus,
  type UsageTimeseries,
} from "../lib/usage";
import { LoadingBlock } from "../components/settings/Spinner";
import { MembersUsageTable } from "../components/dashboard/MembersUsageTable";
import { OrgAnalytics } from "../components/dashboard/OrgAnalytics";
import { RangeControls } from "../components/dashboard/RangeControls";
import { UsageTrend } from "../components/dashboard/UsageTrend";

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  width: 16,
  height: 16,
};

/** The reference heads each card with a glyph; these are the same two. */
function ChartIcon() {
  return (
    <svg {...ICON}>
      <path d="M3 3v18h18" />
      <path d="m7 14 3-4 3 3 4-6" />
    </svg>
  );
}
function GaugeIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      <path d="M3.6 15a9 9 0 1 1 16.8 0" />
      <path d="m14 11 4-4" />
    </svg>
  );
}

/**
 * Today, as the plan's timezone has it — the latest day the picker may offer.
 *
 * The reader's own clock is the wrong ceiling: a machine a day ahead of the
 * server would offer a date the server has no data for, and answer the request
 * with an honest, useless "nothing happened".
 */
function todayIn(timezone: string | undefined): string {
  try {
    // `en-CA` is the shortest route to `yyyy-mm-dd` without hand-assembling
    // the parts, and the locale never reaches the screen.
    return new Date().toLocaleDateString("en-CA", { timeZone: timezone });
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

type Load<T> =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; value: T };

/** A quota line: the figure, and a bar only when there is a limit to fill. */
function Quota({
  used,
  limit,
  unit,
}: {
  used: number;
  limit: number | null;
  unit: string;
}) {
  const percent = usedPercent(used, limit);
  return (
    <div className="quota">
      <div className="quota-figure">
        <span className="quota-used">{used.toLocaleString()}</span>
        {limit !== null ? (
          <span className="quota-limit">
            / {limit.toLocaleString()} {unit}
          </span>
        ) : (
          <span className="quota-limit">{unit}</span>
        )}
      </div>
      {/* No bar when there is no limit — see the header. */}
      {percent !== null ? (
        <div className="quota-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${percent}%` }} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The caller's own cycle: requests, credits, what is left and when it resets.
 *
 * **Which ceilings exist is the whole shape of this block**. Requests are never
 * capped, and on an `org_shared` plan api-v2 withholds the token cap — so each "remaining" line
 * is rendered only against a cap that actually exists. Printing "Unlimited"
 * against a withheld cap would tell a member who *is* capped, by their own
 * credit limit, that they have no ceiling at all. That limit is the fallback
 * denominator, and it is shown only when it says something the lines above do
 * not: on `org_shared` there are no plan caps to restate, and on a per-seat plan
 * an explicitly-set member cap is a genuinely different ceiling. `Unlimited`
 * survives for the one case that earns it — no ceiling anywhere.
 */
function Summary({
  status,
  formatDate,
}: {
  status: UsageStatus;
  formatDate: (iso: string, timezone: string) => string;
}) {
  const { t } = useLocale();
  const { plan, usage, limit, usage_cycle } = status;

  // Requests are counted, never capped (bimgo-ai-api-v3#32), so the requests
  // line has no denominator and no "remaining" figure.
  const tokenLimit = plan.max_tokens_per_month;
  const creditLimit = limit?.effective_limit ?? null;
  // **Spent / limit, not remaining / limit** — the same pair, in the same
  // order, as the members table under Billing & Plan, so an admin comparing the
  // two pages reads one number twice rather than two that need reconciling.
  // `spent` counts wallet draws only, which is exactly what this cap gates.
  const creditSpent = limit?.spent ?? 0;
  // A cap of **0 on a per-seat plan** means the org never granted this member a
  // wallet budget, so the line could only ever read "0 / 0" beside the plan
  // allowance that actually funds them. It stays visible on `org_shared`, where
  // there is no allowance line to carry the meaning: there a 0 cap is the
  // member's whole story, and hiding it would leave the card saying "Unlimited"
  // to someone who cannot spend anything.
  const showCreditLimit =
    creditLimit !== null &&
    (tokenLimit === null || (limit?.limit_source === "member" && creditLimit > 0));
  const hasAnyCeiling = tokenLimit !== null || showCreditLimit;

  return (
    <>
      {/* No plan name. The reference dropped it, and plans are named for
          internal bookkeeping ("pca - paid [a881f06d]") — a string that means
          nothing to the member whose usage this is. The subscription card under
          Billing & Plan is where an admin can read it. */}
      <Quota
        used={usage.requests_used_this_month}
        limit={null}
        unit={t("dashboard.summary.requests")}
      />
      {/* Always rendered: with no cap to divide by, what has been spent is
          still the headline — only the denominator and the bar drop away. */}
      <Quota
        used={usage.tokens_used_this_month}
        limit={tokenLimit}
        unit={t("dashboard.summary.tokens")}
      />
      <div className="quota-foot">
        <div>
          <div className="quota-label">{t("dashboard.summary.remaining")}</div>
          {tokenLimit !== null ? (
            <div className="quota-value">
              {`${remaining(usage.tokens_remaining_this_month)?.toLocaleString() ?? 0} ${t("dashboard.summary.tokens")}`}
            </div>
          ) : null}
          {showCreditLimit ? (
            <>
              <div className="quota-label">{t("dashboard.summary.creditLimit")}</div>
              <div className="quota-value">
                {`${creditSpent.toLocaleString()} / ${
                  creditLimit === null
                    ? t("dashboard.summary.unlimited")
                    : creditLimit.toLocaleString()
                }`}
              </div>
            </>
          ) : null}
          {!hasAnyCeiling ? (
            <div className="quota-value">{t("dashboard.summary.unlimited")}</div>
          ) : null}
        </div>
        <div>
          <div className="quota-label">{t("dashboard.summary.resetsAt")}</div>
          <div className="quota-value">
            {formatDate(usage_cycle.monthly_end, plan.timezone)}
          </div>
        </div>
      </div>
    </>
  );
}

export default function DashboardPane({
  orgId,
  token,
  perms,
}: {
  orgId: string | null;
  token: string | null;
  perms: PermissionsState;
}) {
  const { t, locale } = useLocale();
  const [preset, setPreset] = useState<Range>(30);
  const [custom, setCustom] = useState<UsageDateRange | null>(null);
  const [tab, setTab] = useState<"personal" | "organization">("personal");

  const [status, setStatus] = useState<Load<UsageStatus | null>>({ kind: "loading" });
  const [trend, setTrend] = useState<Load<UsageTimeseries>>({ kind: "loading" });
  const [org, setOrg] = useState<Load<OrgUsageSummary>>({ kind: "loading" });

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const canReadOrg = hasAction(perms, "org:read") && Boolean(orgId);

  /** Every failure the dashboard can meet, in the words it should use. */
  const describe = (error: unknown, forbiddenKey: string): Load<never> => {
    if (statusOf(error) === 403) return { kind: "forbidden" };
    return {
      kind: "error",
      message: error instanceof Error ? error.message : t(forbiddenKey),
    };
  };

  useEffect(() => {
    if (!isConfigured()) return;
    let cancelled = false;
    setStatus({ kind: "loading" });
    // The org is part of the question, not context: it decides the billing
    // mode, and without it api-v2 answers as though every plan were per-seat.
    getUsageStatus(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setStatus({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setStatus(describe(e, "dashboard.summary.loadFailed")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // One selection, two parameterisations: a preset asks for `days`, a picked
  // window for `from`/`to`. Never both — see `getUsageTimeseries`.
  const selection = custom ?? preset;

  useEffect(() => {
    if (!isConfigured()) return;
    let cancelled = false;
    setTrend({ kind: "loading" });
    getUsageTimeseries(tokenRef.current, selection, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setTrend({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setTrend(describe(e, "dashboard.chart.loadFailed")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, custom?.from, custom?.to]);

  // Only the members table now: the org's own figures and trend come from
  // `usage-dashboard`, which `OrgAnalytics` reads for itself.
  useEffect(() => {
    if (!canReadOrg || !orgId || !isConfigured()) return;
    let cancelled = false;
    setOrg({ kind: "loading" });
    getOrgUsage(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setOrg({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setOrg(describe(e, "dashboard.admin.summary.loadFailed")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadOrg, orgId]);

  const formatDate = (iso: string, timezone: string) => {
    try {
      return new Date(iso).toLocaleDateString(locale === "zh" ? "zh-TW" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        // The server bucketed against the plan's timezone; showing the viewer's
        // would put the reset on a different calendar day for anyone overseas.
        timeZone: timezone,
      });
    } catch {
      return iso;
    }
  };

  /**
   * What the trend is showing, in words, above the chart.
   *
   * Taken from the **points that came back** rather than from the control: a
   * preset says "last 30 days" while the data may start later, and the label
   * that matters is the one describing what is drawn. Only when there are no
   * points does it fall back to naming the selection.
   */
  const trendPeriod = (() => {
    const points = trend.kind === "loaded" ? trend.value.points : [];
    const first = points[0]?.date;
    const last = points[points.length - 1]?.date;
    if (first && last) return formatDateRange(first, addOneDay(last), locale);
    if (custom) return formatDateRange(custom.from, custom.to, locale);
    return t(`dashboard.chart.range.${preset}d`);
  })();

  const failure = (state: Load<unknown>, key: string) =>
    state.kind === "forbidden" ? (
      <p className="settings-note">{t("error.forbidden")}</p>
    ) : (
      <p className="settings-msg err" role="alert">
        {t(key)}
      </p>
    );

  const personal = (
    <>
      <div className="settings-card">
        <h3 className="card-title">
          <span className="card-title-icon">
            <GaugeIcon />
          </span>
          {t("dashboard.summary.title")}
        </h3>
        {status.kind === "loading" ? (
          <LoadingBlock size={24} />
        ) : status.kind !== "loaded" ? (
          failure(status, "dashboard.summary.loadFailed")
        ) : !status.value ? (
          <p className="settings-note">{t("dashboard.summary.noPlan")}</p>
        ) : (
          <Summary status={status.value} formatDate={formatDate} />
        )}
      </div>

      <div className="settings-card">
        <div className="card-head-row">
          <div>
            <h3 className="card-title">
              <span className="card-title-icon">
                <ChartIcon />
              </span>
              {t("dashboard.chart.title")}
            </h3>
            <p className="settings-note">{trendPeriod}</p>
          </div>
          <RangeControls
            preset={preset}
            custom={custom}
            // The personal trend has no `as_of` of its own; the timeseries
            // reports the plan's timezone, so today in it is the right ceiling.
            asOf={trend.kind === "loaded" ? todayIn(trend.value.timezone) : null}
            onPreset={setPreset}
            onCustom={setCustom}
          />
        </div>
        {trend.kind === "loading" ? (
          <LoadingBlock size={24} />
        ) : trend.kind !== "loaded" ? (
          failure(trend, "dashboard.chart.loadFailed")
        ) : (
          <UsageTrend points={trend.value.points} />
        )}
      </div>
    </>
  );

  /**
   * The organisation tab: the analytics, then every member's usage.
   *
   * This replaced a summary card and a trend card that read one endpoint each
   * and totalled the member rows on the client. `usage-dashboard` answers the
   * same questions on the server, over a range rather than only this cycle, and
   * adds the two the client could not compute at all — active users and cloud
   * skills.
   */
  const organization = (
    <>
      {orgId ? <OrgAnalytics orgId={orgId} token={token} /> : null}
      {org.kind === "loading" ? (
        <div className="settings-card">
          <LoadingBlock size={24} />
        </div>
      ) : org.kind !== "loaded" ? (
        <div className="settings-card">{failure(org, "dashboard.admin.summary.loadFailed")}</div>
      ) : (
        <MembersUsageTable summary={org.value} />
      )}
    </>
  );

  return (
    <section data-pane="dashboard">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2>{t("dashboard.title")}</h2>
        </div>
      </div>
      <p className="settings-note">{t("dashboard.subtitle")}</p>

      {/* Tabs only when there is a second tab. A lone "Personal Usage" tab is
          furniture — the reference drops them for non-admins too. */}
      {canReadOrg ? (
        <div className="settings-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "personal"}
            className={`settings-tab${tab === "personal" ? " is-active" : ""}`}
            onClick={() => setTab("personal")}
          >
            {t("dashboard.section.personal")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "organization"}
            className={`settings-tab${tab === "organization" ? " is-active" : ""}`}
            onClick={() => setTab("organization")}
          >
            {t("dashboard.section.organization")}
          </button>
        </div>
      ) : null}

      {canReadOrg && tab === "organization" ? organization : personal}
    </section>
  );
}
